import { useMutation } from '@apollo/client'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import {
  Button,
  ButtonColor,
  NotificationType,
  RadioGroup,
} from '../../../components'
import { UPDATE_BRAND } from '../../../gql'
import { Brand, BrandOnboardingStep, Industry } from '../../../gql/types'
import { AnalyticsEvent, trackEvent } from '../../../utils/analytics'
import { Bullets } from '../components/Bullets'
import { Steps } from '../components/Steps'
import { useNotification } from '../../../utils/hooks'
import { mapIndustryToText } from '../../../utils/mappers'

interface EditBrandIndustryFormData {
  industry: Industry
}

interface Props {
  brand: Brand
}

const SetBusinessIndustry = ({ brand }: Props) => {
  const { addNotification } = useNotification()
  const [updateBrand, { loading }] = useMutation(UPDATE_BRAND)

  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm({
    defaultValues: {
      industry: brand?.industry ?? null,
    },
  })

  useEffect(() => {
    trackEvent(AnalyticsEvent.viewBrandsOnboardingSetBrandIndustry)
  }, [])

  const onSubmit = async ({ industry }: EditBrandIndustryFormData) => {
    try {
      await updateBrand({
        variables: {
          id: brand.id,
          input: {
            industry,
            onboarding: BrandOnboardingStep.setProductDetails,
          },
        },
      })
    } catch (error) {
      console.log('@error SetBusinessIndustry', error.message)
      addNotification(error.message, NotificationType.error)
    }
  }

  const goBack = async () => {
    try {
      await updateBrand({
        variables: {
          id: brand.id,
          input: {
            onboarding: BrandOnboardingStep.setWebsite,
          },
        },
      })
    } catch (error) {
      addNotification(error.message, NotificationType.error)
    }
  }

  return (
    <div className='min-h-screen flex flex-col'>
      <div className='flex-grow'>
        <Steps
          brand={brand}
          stepCurrent={BrandOnboardingStep.setBusinessIndustry}
        />

        <div className='min-h-full flex flex-col justify-center pt-4 px-4 md:mt-8 lg:px-8 md:mt-20'>
          <div className='sm:mx-auto sm:w-full sm:max-w-2xl'>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className='md:shadow rounded-t-md sm:overflow-hidden'>
                <div className='py-5 md:bg-white space-y-6 sm:p-6'>
                  <div className='mx-auto'>
                    <h2 className='mt-0 md:text-center text-3xl font-extrabold text-gray-900'>
                      Business industry
                    </h2>
                    <p className='mt-2 md:text-center text-sm text-gray-600'>
                      What products does your business sell? Choose the closest
                      industry.
                    </p>
                  </div>

                  <div className='grid gap-6'>
                    <div className='col-span-3 sm:col-span-2'>
                      <Controller
                        name='industry'
                        control={control}
                        rules={{ required: `Please select an industry.` }}
                        render={({ field }) => (
                          <RadioGroup
                            options={Object.values(Industry).map(
                              (industry) => ({
                                type: industry,
                                name: mapIndustryToText(industry),
                              }),
                            )}
                            field={field}
                            error={errors?.industry?.message}
                            className='grid grid-cols-2 sm:grid-cols-3'
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className='flex justify-between md:shadow rounded-b-md py-3 md:bg-gray-50 text-right sm:px-6'>
                <Button
                  title='Go back'
                  type='button'
                  colour={ButtonColor.white}
                  loading={loading}
                  disabled={loading}
                  onClick={goBack}
                />

                <Button
                  title='Continue'
                  type='submit'
                  loading={loading}
                  disabled={loading}
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      <Bullets stepCurrent={BrandOnboardingStep.setBusinessIndustry} />
    </div>
  )
}

export { SetBusinessIndustry }
